import type { FlightDatesResult, FlightLookupDateKind, FlightLookupResult, FlightLookupRole, TravelDirection } from "@seagames/contracts";
import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";

/**
 * `GET /flights/lookup?flight=MH803&date=2026-11-03&direction=ARRIVAL[&date_kind=SERVICE]` (M9).
 *
 * An accelerator, never a gate (PLAN T5): `found: false` (tracking off, or
 * the provider does not know the flight) and a 503 both mean "let the person
 * type the time". The API counts every call that reaches the provider against
 * a per-login hourly limit, so callers ask once per (flight, date, kind, direction)
 * — on blur / Enter / a picked day, never per keystroke (`useFlightLookup` dedupes).
 * `date_kind`: LEG (default) = the leg's typed travel date; SERVICE = the
 * flight's departure date, a day picked from `GET /flights/dates`.
 * `role=CONNECTION`: a transit leg's feeder / onward flight — never
 * NOT_OUR_AIRPORT, no suggested entry point; `direction` stays the leg's.
 *
 * Not a react-query query on purpose: a lookup may write the `flights` row
 * (the server stores what the provider said), and its result is copied into
 * the form, not rendered from a cache.
 */
export const lookupFlight = (q: { flight: string; date: string; direction: TravelDirection; date_kind?: FlightLookupDateKind; role?: FlightLookupRole }, signal?: AbortSignal) =>
	api.get<FlightLookupResult>("/flights/lookup", { query: q, signal });

/** `GET /flights/dates?flight=MH602&direction=ARRIVAL` — the departure dates this flight number operates in the travel window. */
export const fetchFlightDates = (q: { flight: string; direction: TravelDirection }, signal?: AbortSignal) =>
	api.get<FlightDatesResult>("/flights/dates", { query: q, signal });

export const flightKeys = {
	dates: (flight: string, direction: TravelDirection) => ["flights", "dates", flight, direction] as const,
};

/**
 * The days a flight number flies, cached for an hour (the server keeps its
 * own copy 12 h). `flight` is the compact designator ("MH602"), so "mh 602"
 * and "MH602" share one entry. No retries: a 503 or 429 is a fallback to
 * typed dates, not something to hammer; the caller re-asks on the next blur.
 */
export const flightDatesOptions = (flight: string, direction: TravelDirection) =>
	queryOptions({
		queryKey: flightKeys.dates(flight, direction),
		queryFn: ({ signal }) => fetchFlightDates({ flight, direction }, signal),
		staleTime: 60 * 60 * 1000,
		retry: false,
	});
